import { Callout, Collapse, Intent, PanelProps } from '@blueprintjs/core';
import * as React from 'react';

import { DatabaseCard } from 'src/components/cards/DatabaseCard';
import { ModelCard } from 'src/components/cards/ModelCard';
import { TEST_IDS } from 'src/constants/testIds';
import { useImportModel } from 'src/containers/ProjectModelDialog/hooks/useImportModelValue';
import { ModelSelectItem } from 'src/selectors/modelItems';
import * as copy from 'src/templates/copy';
import { checkImportModelLimits } from 'src/utils/checkImportModelLimits';

export type ImportModelPanelProps = {
  // empty
};

export const ImportModelPanel: React.VFC<
  PanelProps<ImportModelPanelProps>
> = () => {
  const {
    databaseId,
    selectedModels,
    setDatabaseId,
    setSelectedModels,
    setPendingModelData,
    setIsModelDataLoading,
    pendingModelData,
    unimportableNamedGraphIris,
  } = useImportModel();

  const handleSetSelectedModels = React.useCallback(
    (newModels: ModelSelectItem[]) => {
      setSelectedModels(newModels);
      setPendingModelData({});
      setIsModelDataLoading(false);
    },
    [setSelectedModels, setPendingModelData, setIsModelDataLoading]
  );

  const isOverLimit = React.useMemo(
    () => checkImportModelLimits(pendingModelData),
    [pendingModelData]
  );

  React.useEffect(() => {
    setPendingModelData({});
    setIsModelDataLoading(false);
  }, [databaseId, setPendingModelData, setIsModelDataLoading]);

  return (
    <Callout className="sd-model-import-panel">
      <Collapse isOpen={Boolean(isOverLimit)}>
        <Callout
          data-testid={TEST_IDS.projectModelDialog.validation.largeModelLimit}
          intent={Intent.WARNING}
        >
          {copy.components.projectModelDialog.validation.largeModelLimit}
        </Callout>
      </Collapse>
      <Collapse isOpen={Boolean(unimportableNamedGraphIris.length)}>
        <Callout
          data-testid={TEST_IDS.projectModelDialog.validation.comboModels}
          intent={Intent.WARNING}
        >
          {copy.components.projectModelDialog.validation.comboModelContainsExtraGraphs(
            unimportableNamedGraphIris
          )}
        </Callout>
      </Collapse>
      <div className="sd-model-import-panel__cards">
        <DatabaseCard databaseId={databaseId} setDatabaseId={setDatabaseId} />
        <ModelCard
          databaseId={databaseId}
          selectedModels={selectedModels}
          setSelectedModels={handleSetSelectedModels}
        />
      </div>
    </Callout>
  );
};
